/**
 * Walkthrough Guide Component
 * 
 * An in-app guide that appears alongside navigation sections to guide users
 * through the application features. Based on Figma design and tutorial modal behavior.
 */

import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { flushSync } from 'react-dom';
import {
  Box,
  Paper,
  Typography,
  Fade,
  Tooltip,
  InputLabel as MuiInputLabel,
} from '@mui/material';
import { WalkthroughTitle, Input, InputLabel, SkipGuide } from '../atoms';
import LocationDropdown from '../molecules/LocationDropdown';
import {
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import ReducersIndex from 'reduxModules/ducks';
import ToolContext from 'components/services/ToolContext';
import tutorialSteps, { tutorialActions } from '../../toolConstants/conferenceroomDesigner/tutorial';
import { useToast } from '../../contexts/ToastContext';
import type { RootState } from 'types/redux/state';

interface WalkthroughGuideProps {
  onComplete?: () => void;
  onSkip?: () => void;
  startFromMenu?: string; // Start from specific menu when launched via 'i' button
  showOnDesignsPage?: boolean; // Show on designs page instead of main page
}

interface PreciseBackdropProps {
  spotlightTarget: string | string[] | null;
  onSpotlightReady?: () => void;
}

const PreciseBackdrop: React.FC<PreciseBackdropProps> = ({ spotlightTarget, onSpotlightReady }) => {
  const [activeElementBounds, setActiveElementBounds] = useState<DOMRect | null>(null);

  useEffect(() => {
    // If no target, show full backdrop without cutout
    if (!spotlightTarget) {
      setActiveElementBounds(null);
      return;
    }

    let debounceTimer: NodeJS.Timeout | null = null;

    const updateBounds = () => {
      // Handle arrays of targets
      const targets = Array.isArray(spotlightTarget) ? spotlightTarget : [spotlightTarget];
      const elements: HTMLElement[] = [];

      // Collect all existing elements
      for (const target of targets) {
        let activeElementId: string;

        // Special handling for specific targets
        if (target === 'actionButtons') {
          activeElementId = 'actionButtons';
        } else if (target === 'projectName') {
          activeElementId = 'projectName';
        } else if (target === 'floor_plan') {
          activeElementId = 'floor_plan';
        } else if (target === 'equipment_list') {
          activeElementId = 'equipment_list';
        } else {
          activeElementId = target;
        }

        const element = document.getElementById(activeElementId);
        if (element) {
          elements.push(element);
        }
      }
      
      // Special handling for Request Quote step - highlight all action buttons
      // Updated to include Save, Duplicate, Reset, Export, and Request Quote buttons
      if (targets.includes('designContainerQuoteButton') && targets.includes('exportButton')) {
        // Keep all the action buttons: Save, Duplicate, Export, Reset, and Request Quote
        const filteredElements = elements.filter(el =>
          el.id === 'designContainerQuoteButton' ||
          el.id === 'exportButton' ||
          el.id === 'duplicateButton' ||
          el.id === 'resetButton' ||
          el.id === 'saveButton'
        );
        if (filteredElements.length > 0) {
          elements.length = 0; // Clear array
          elements.push(...filteredElements);
        }
      }
      
      if (elements.length > 0) {
        // Check for open dropdowns/selects within or near the target element
        // Look for MUI Menu/Popover elements that are currently open
        const openMenus = document.querySelectorAll('.MuiPopover-root, .MuiMenu-root, .MuiPopper-root');
        let hasOpenDropdown = false;
        let dropdownBottom = -Infinity;
        
        // Get the original bounds first
        let originalBounds: DOMRect;
        if (elements.length > 1) {
          // Multiple elements - get combined bounds
          let minTop = Infinity, minLeft = Infinity;
          let maxBottom = -Infinity, maxRight = -Infinity;

          for (const element of elements) {
            const rect = element.getBoundingClientRect();
            minTop = Math.min(minTop, rect.top);
            minLeft = Math.min(minLeft, rect.left);
            maxBottom = Math.max(maxBottom, rect.bottom);
            maxRight = Math.max(maxRight, rect.right);
          }

          // Check if this is Request Quote step and adjust bounds
          const isRequestQuoteStep = targets.includes('designContainerQuoteButton') && targets.includes('exportButton') && targets.includes('saveButton');
          const adjustedTop = isRequestQuoteStep ? minTop - 5 : minTop; // Subtract 5px for Request Quote
          const adjustedLeft = isRequestQuoteStep ? minLeft - 10 : minLeft; // Subtract 10px for Request Quote
          const adjustedRight = isRequestQuoteStep ? maxRight + 10 : maxRight; // Add 10px for Request Quote
          const adjustedBottom = isRequestQuoteStep ? maxBottom - 3 : maxBottom; // Subtract 3px for Request Quote

          originalBounds = {
            top: adjustedTop,
            left: adjustedLeft,
            bottom: adjustedBottom,
            right: adjustedRight,
            width: adjustedRight - adjustedLeft,
            height: adjustedBottom - adjustedTop, // Adjust height based on adjusted top and bottom
            x: adjustedLeft,
            y: adjustedTop,
            toJSON: () => ({})
          } as DOMRect;
        } else {
          // Single element
          originalBounds = elements[0].getBoundingClientRect();
        }
        
        // Check if any open menus are related to our target elements
        openMenus.forEach((menu) => {
          const menuElement = menu as HTMLElement;
          // Check if the menu is visible and has content
          if (menuElement.style.opacity !== '0' &&
              menuElement.style.visibility !== 'hidden' &&
              !menuElement.style.display?.includes('none')) {
            // Check if it contains a menu list or select options
            const menuList = menuElement.querySelector('.MuiMenu-list, .MuiList-root, .MuiPaper-root');
            if (menuList) {
              const menuRect = menuElement.getBoundingClientRect();
              // Only consider dropdowns that are positioned near our target (within reasonable horizontal range)
              if (menuRect.left >= originalBounds.left - 50 &&
                  menuRect.right <= originalBounds.right + 50) {
                hasOpenDropdown = true;
                dropdownBottom = Math.max(dropdownBottom, menuRect.bottom);
              }
            }
          }
        });
        
        // Also check for any dropdown within the target element itself
        for (const element of elements) {
          // Check if the element or its children contain an expanded dropdown
          const selects = element.querySelectorAll('select, [role="combobox"], [aria-haspopup="listbox"], .MuiSelect-root');
          selects.forEach((select) => {
            const selectElement = select as HTMLElement;
            // Check aria-expanded attribute
            if (selectElement.getAttribute('aria-expanded') === 'true') {
              hasOpenDropdown = true;
              // Try to find the associated dropdown menu
              const menuId = selectElement.getAttribute('aria-controls');
              if (menuId) {
                const menu = document.getElementById(menuId);
                if (menu) {
                  const menuRect = menu.getBoundingClientRect();
                  dropdownBottom = Math.max(dropdownBottom, menuRect.bottom);
                }
              }
            }
          });
        }
        
        // Create bounds - keep original top and width, only extend bottom if dropdown is open
        if (elements.length > 1 || hasOpenDropdown) {
          const padding = 10; // Add padding around the highlighted area
          const horizontalPadding = 12; // 2px wider on each side

          // Keep the original top position fixed when dropdown expands
          const fixedTop = hasOpenDropdown ? originalBounds.top : originalBounds.top - padding;

          // Only extend bottom if dropdown is open and extends below original element
          const finalBottom = hasOpenDropdown && dropdownBottom > originalBounds.bottom
            ? dropdownBottom + padding
            : originalBounds.bottom + padding;

          const combinedBounds = {
            top: fixedTop,
            left: hasOpenDropdown ? originalBounds.left : originalBounds.left - horizontalPadding,
            bottom: finalBottom,
            right: hasOpenDropdown ? originalBounds.right : originalBounds.right + horizontalPadding,
            width: hasOpenDropdown ? originalBounds.width : originalBounds.width + (horizontalPadding * 2),
            height: finalBottom - fixedTop,
            x: hasOpenDropdown ? originalBounds.left : originalBounds.left - horizontalPadding,
            y: fixedTop,
            toJSON: () => ({})
          } as DOMRect;

          setActiveElementBounds(combinedBounds);

          // Notify parent that spotlight is ready for multiple elements
          if (onSpotlightReady) {
            onSpotlightReady();
          }
        } else {
          // Single element without dropdown
          const element = elements[0];
          let targetElement = element;

          // For name design, we want to highlight the entire project section, not just the text
          if (spotlightTarget === 'projectName' && element.id === 'projectName') {
            const parentSection = element.parentElement as HTMLElement;
            if (parentSection) {
              targetElement = parentSection;
            }
          }

          const bounds = targetElement.getBoundingClientRect();
          setActiveElementBounds(bounds);

          // Notify parent that spotlight is ready
          if (onSpotlightReady) {
            onSpotlightReady();
          }
        }
      } else {
        setActiveElementBounds(null);
      }
    };

    // Debounced version of updateBounds to prevent excessive calls
    const debouncedUpdateBounds = () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = setTimeout(updateBounds, 50); // 50ms debounce
    };

    // Call updateBounds immediately on mount
    updateBounds();

    // Retry finding the element if it's not immediately available (especially for projectName)
    if (spotlightTarget === 'projectName' && !document.getElementById('projectName')) {
      const retryTimer = setTimeout(updateBounds, 100);

      // Another retry if still not found
      const retryTimer2 = setTimeout(updateBounds, 250);

      return () => {
        clearTimeout(retryTimer);
        clearTimeout(retryTimer2);
        if (debounceTimer) {
          clearTimeout(debounceTimer);
        }
      };
    }

    // Update bounds when window resizes (debounced)
    window.addEventListener('resize', debouncedUpdateBounds);

    // Use debounced version for MutationObserver to prevent excessive calls
    const observer = new MutationObserver(debouncedUpdateBounds);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    });

    return () => {
      window.removeEventListener('resize', debouncedUpdateBounds);
      observer.disconnect();
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [spotlightTarget]);

  // If no spotlight target, show full backdrop without cutout
  if (!spotlightTarget) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: theme => theme.palette.mode === 'dark' 
            ? 'rgba(0, 0, 0, 0.8)' 
            : 'rgba(0, 0, 0, 0.6)',
          zIndex: 1200,
          pointerEvents: 'none',
        }}
      />
    );
  }

  if (!activeElementBounds) {
    return (
      // Fallback: cover everything if we can't find the active element
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: theme => theme.palette.mode === 'dark' 
            ? 'rgba(0, 0, 0, 0.6)' 
            : 'rgba(0, 0, 0, 0.3)',
          zIndex: 1200,
          pointerEvents: 'none',
        }}
      />
    );
  }

  const { top, left, width, height } = activeElementBounds;

  // Check if the spotlight target is in the main canvas area (actionButtons) or nav area
  // Handle both single targets and arrays
  const targetsArray = Array.isArray(spotlightTarget) ? spotlightTarget : [spotlightTarget];
  const isCanvasElement = targetsArray.includes('actionButtons') || 
                          targetsArray.includes('designContainerQuoteButton') || 
                          targetsArray.includes('exportButton');
  const isMyDesignsButton = targetsArray.includes('myDesignsButton');
  const isExportButton = targetsArray.includes('exportButton');

  // Special handling for myDesignsButton and exportButton - exact fit without adjustments
  if (isMyDesignsButton || isExportButton) {
    return (
      <>
        {/* Single backdrop with exact fit for My Designs or Export button */}
        <Box
          sx={{
            position: 'fixed',
            top: `${top}px`,
            left: `${left}px`,
            width: `${width}px`,
            height: `${height}px`,
            borderRadius: '6px', // Match button's border radius
            zIndex: 1200,
            pointerEvents: 'none',
            // Create backdrop using large box-shadow
            boxShadow: theme => `0 0 0 ${Math.max(window.innerWidth, window.innerHeight)}px ${
              theme.palette.mode === 'dark' 
                ? 'rgba(0, 0, 0, 0.6)' 
                : 'rgba(0, 0, 0, 0.3)'
            }`,
          }}
        />
      </>
    );
  }

  if (isCanvasElement) {
    // For canvas elements like actionButtons, create backdrop with rounded cutout using box-shadow
    const borderRadius = 12; // 12px border radius for rounded corners
    const padding = 8; // Extra padding around the cutout

    // Check if this is the Request Quote step (which targets individual buttons)
    // Request Quote step includes: saveButton, exportButton, designContainerQuoteButton, duplicateButton, resetButton
    const isRequestQuoteStep = targetsArray.includes('saveButton') && targetsArray.includes('exportButton') && targetsArray.includes('designContainerQuoteButton');
    const topPadding = isRequestQuoteStep ? padding + 5 : padding; // Add 5px extra top padding for Request Quote step
    const horizontalPadding = isRequestQuoteStep ? padding + 5 : padding; // Add 5px extra left/right padding for Request Quote step

    // Adjust cutout bounds with padding
    const cutoutTop = top - topPadding;
    const cutoutLeft = left - horizontalPadding;
    const cutoutWidth = width + (horizontalPadding * 2);
    const cutoutHeight = height + (topPadding) + padding; // Adjust height to account for top padding
    
    return (
      <>
        {/* Single backdrop with large box-shadow to create cutout effect */}
        <Box
          sx={{
            position: 'fixed',
            top: `${cutoutTop}px`,
            left: `${cutoutLeft}px`,
            width: `${cutoutWidth}px`,
            height: `${cutoutHeight}px`,
            borderRadius: `${borderRadius}px`,
            zIndex: 1200,
            pointerEvents: 'none',
            // Create backdrop using large box-shadow
            boxShadow: theme => `0 0 0 ${Math.max(window.innerWidth, window.innerHeight)}px ${
              theme.palette.mode === 'dark'
                ? 'rgba(0, 0, 0, 0.85)'
                : 'rgba(0, 0, 0, 0.3)'
            }`,
            // Add a subtle border glow in dark mode for better visibility
            '&::after': theme => theme.palette.mode === 'dark' ? {
              content: '""',
              position: 'absolute',
              top: -2,
              left: -2,
              right: -2,
              bottom: -2,
              borderRadius: `${borderRadius + 2}px`,
              border: '2px solid rgba(255, 255, 255, 0.1)',
              pointerEvents: 'none',
            } : {},
          }}
        />
      </>
    );
  }

  // Check if this is the projectName section
  const isProjectNameSection = targetsArray.includes('projectName');

  // Add padding around the spotlight for better visual appearance
  let adjustedTop = top - 10;
  let adjustedHeight = height + 20;
  let adjustedLeft = left - 12; // 2px wider on each side
  let adjustedWidth = width + 24; // 12px padding on each side

  // Special handling for projectName section - set fixed width to 296px
  if (isProjectNameSection) {
    const fixedWidth = 296;
    adjustedTop = top - 10; // 10px padding on top
    adjustedHeight = height + 20; // Add 20px total height (10px top + 10px bottom)
    adjustedWidth = fixedWidth; // Fixed width for Name Design spotlight
    adjustedLeft = left - ((fixedWidth - width) / 2); // Center the spotlight on the element

    // Use box-shadow technique for rounded corners on projectName
    return (
      <>
        {/* Single backdrop with rounded cutout for projectName */}
        <Box
          sx={{
            position: 'fixed',
            top: `${adjustedTop}px`,
            left: `${adjustedLeft}px`,
            width: `${adjustedWidth}px`,
            height: `${adjustedHeight}px`,
            borderRadius: '8px', // Rounded corners
            zIndex: 1200,
            pointerEvents: 'none',
            // Create backdrop using large box-shadow
            boxShadow: theme => `0 0 0 ${Math.max(window.innerWidth, window.innerHeight)}px ${
              theme.palette.mode === 'dark'
                ? 'rgba(0, 0, 0, 0.6)'
                : 'rgba(0, 0, 0, 0.3)'
            }`,
          }}
        />
      </>
    );
  }
  
  // For nav sections, use box-shadow technique for rounded corners
  return (
    <>
      {/* Single backdrop with rounded cutout for nav sections */}
      <Box
        sx={{
          position: 'fixed',
          top: `${adjustedTop}px`,
          left: `${adjustedLeft}px`,
          width: `${adjustedWidth}px`,
          height: `${adjustedHeight}px`,
          borderRadius: '8px', // Rounded corners for nav sections
          zIndex: 1200,
          pointerEvents: 'none',
          // Create backdrop using large box-shadow
          boxShadow: theme => `0 0 0 ${Math.max(window.innerWidth, window.innerHeight)}px ${
            theme.palette.mode === 'dark' 
              ? 'rgba(0, 0, 0, 0.6)' 
              : 'rgba(0, 0, 0, 0.3)'
          }`,
        }}
      />
    </>
  );
};

const WalkthroughGuide: React.FC<WalkthroughGuideProps> = ({ onComplete, onSkip, startFromMenu, showOnDesignsPage }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const toolContext = useContext(ToolContext) as any;
  const { showSuccess } = useToast();
  
  
  // Get Redux state
  const tutorialStepFromRedux = useSelector((state: RootState) => state.ui.tutorialStep);
  const tutorialConfig = useSelector((state: RootState) => state.ui.tutorialConfig);
  const spotlightTarget = useSelector((state: RootState) => state.ui.spotlightTarget);
  const isFirstTimeUserFromRedux = useSelector((state: RootState) => state.ui.isFirstTimeUser);
  
  // Local state to maintain backdrop visibility independently of Redux spotlight state
  const [shouldShowBackdrop, setShouldShowBackdrop] = useState(false);
  
  // Double-check localStorage in case Redux state is stale
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(() => {
    const hasCompleted = localStorage.getItem('hasCompletedTutorial') === 'true';
    return !hasCompleted;
  });
  
  // Sync with localStorage on mount and initialize backdrop visibility
  useEffect(() => {
    const hasCompleted = localStorage.getItem('hasCompletedTutorial') === 'true';
    setIsFirstTimeUser(!hasCompleted);

    // Sync Redux state with localStorage
    dispatch(ReducersIndex.uiReducers.setIsFirstTimeUser(!hasCompleted));

    // Initialize backdrop visibility - show backdrop when walkthrough is active for first-time users
    if (!hasCompleted) {
      setShouldShowBackdrop(true);
    }

    // If localStorage says completed but Redux says first-time, update Redux
    if (hasCompleted && isFirstTimeUserFromRedux) {
      dispatch(ReducersIndex.uiReducers.setIsFirstTimeUser(false));
    }
  }, [dispatch, isFirstTimeUserFromRedux]);

  // ADDED: Reset isVisible when spotlight changes and tutorial is re-triggered
  useEffect(() => {
    const hasCompleted = localStorage.getItem('hasCompletedTutorial') === 'true';
    if (!hasCompleted && spotlightTarget) {
      setIsVisible(true);
    }
  }, [spotlightTarget]);
  
  
  
  // Close any open tutorial modal when WalkthroughGuide mounts
  useEffect(() => {
    dispatch(ReducersIndex.uiReducers.closeModal('tutorial'));
  }, [dispatch, tutorialConfig, tutorialStepFromRedux]);
  const activeTab = useSelector((state: RootState) => state.container.activeTab);
  const area = useSelector((state: RootState) => state.container.areas[activeTab]);
  
  // Use a ref to access area without making it a dependency
  const areaRef = useRef(area);
  useEffect(() => {
    areaRef.current = area;
  }, [area]);
  
  // Track the last processed step to prevent reprocessing
  const lastProcessedStepRef = useRef<number>(-1);
  
  // Use tutorialConfig if available, otherwise use default tutorialSteps
  const steps = tutorialConfig || tutorialSteps;
  
  // Find starting step based on menu if provided or Redux state
  const getStartingStep = () => {
    // Always start at the first screen when rendered in DesignsList
    if (showOnDesignsPage) {
      return 0;
    }
    
    // Check if we're continuing from the welcome screen
    const continueStep = sessionStorage.getItem('tutorialStep');
    
    if (continueStep) {
      const step = parseInt(continueStep, 10);
      return step;
    }
    
    if (startFromMenu) {
      const stepIndex = steps.findIndex((step: any) => step.menu === startFromMenu);
      return stepIndex >= 0 ? stepIndex : 0;
    }
    if (tutorialStepFromRedux !== undefined) {
      return tutorialStepFromRedux;
    }
    return 0;
  };
  
  const initialStep = getStartingStep();
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(true);

  // ADDED: Sync local currentStep with Redux tutorialStep when it changes
  useEffect(() => {
    if (tutorialStepFromRedux !== undefined && tutorialStepFromRedux !== currentStep) {
      setCurrentStep(tutorialStepFromRedux);
    }
  }, [tutorialStepFromRedux, currentStep]);
  const [designName, setDesignName] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [locationExpanded, setLocationExpanded] = useState(false);
  const [isPositioningNavSection, setIsPositioningNavSection] = useState(false);
  const [hideGuideModal, setHideGuideModal] = useState(false);
  const [fadeGuide, setFadeGuide] = useState(true); // Start with fade in
  const [isInTransition, setIsInTransition] = useState(false); // Track transition state
  const [navTooltipReady, setNavTooltipReady] = useState(false); // Track when nav tooltip position is ready

  // Use ref instead of state to avoid stale closure issues with callbacks
  const pendingTooltipSetupRef = useRef<(() => void) | null>(null);
  const positioningTimeoutRef = useRef<NodeJS.Timeout | null>(null);


  // Ensure currentStep is within bounds
  const safeCurrentStep = Math.min(Math.max(0, currentStep), steps.length - 1);
  const currentGuideStep = steps[safeCurrentStep];

  // Helper function to calculate tooltip position for nav sections, name design, and action buttons
  const calculateTooltipPosition = useCallback(() => {
    if (!anchorEl || !spotlightTarget) return null;

    // anchorEl can be either a real HTMLElement or a virtual anchor object
    const rect = anchorEl.getBoundingClientRect();

    // For virtual anchors, check the id property
    const anchorId = (anchorEl as any)?.id;

    // For HTMLElements, check element.id
    const element = anchorEl instanceof HTMLElement ? anchorEl : null;
    const elementId = element?.id || '';

    const isNavSection = ['floor_plan', 'ceiling', 'furnishing', 'acoustics', 'microphones', 'speakers', 'camera', 'equipment_list'].includes(elementId);
    const isProjectName = elementId === 'projectName' || (element?.querySelector && element.querySelector('#projectName'));
    const isActionButtons = elementId === 'actionButtons';

    // Check if this is Project actions (multiple buttons)
    // The anchorEl has id 'requestQuoteAnchor' for this step (virtual anchor)
    const isProjectActions = anchorId === 'requestQuoteAnchor';

    // Check if this is My Designs button (Access designs step)
    const isMyDesignsButton = anchorId === 'myDesignsButtonBottom' || elementId === 'myDesignsButton';

    if (isNavSection) {
      // Position tooltip to the right of the spotlight, with top aligned
      // Spotlight has -10px top padding and +12px right padding
      // So we need to add the spotlight's right padding (12px) + desired gap (8px)
      return {
        top: rect.top - 10, // Align with spotlight top (which has -10px padding)
        left: rect.right + 12 + 8, // Element right + spotlight padding (12px) + gap (8px) = 20px total
      };
    }

    if (isProjectName) {
      // Name Design spotlight has fixed width of 296px and is centered on the element
      // Spotlight has -10px top padding
      const fixedWidth = 296;
      const spotlightLeft = rect.left - ((fixedWidth - rect.width) / 2);
      const spotlightRight = spotlightLeft + fixedWidth;

      return {
        top: rect.top - 10, // Align with spotlight top (which has -10px padding)
        left: spotlightRight + 8, // Spotlight right edge + gap (8px)
      };
    }

    if (isActionButtons) {
      // Action buttons spotlight has 8px padding on all sides
      const spotlightPadding = 8;
      const spotlightTop = rect.top - spotlightPadding;
      const spotlightRight = rect.right + spotlightPadding;

      return {
        top: spotlightTop, // Align with spotlight top (which has -8px padding)
        left: spotlightRight + 8, // Spotlight right edge + gap (8px)
      };
    }

    if (isProjectActions) {
      // Project actions - multiple buttons with special padding
      // Position tooltip ABOVE the spotlight, with left edges aligned and 8px gap
      const duplicateButton = document.getElementById('duplicateButton');

      if (duplicateButton) {
        const duplicateRect = duplicateButton.getBoundingClientRect();

        // Spotlight has 13px padding on sides and 13px on top/bottom (8 + 5)
        const spotlightPadding = 13;
        const spotlightLeft = duplicateRect.left - spotlightPadding;
        const spotlightTop = duplicateRect.top - spotlightPadding;

        // Position tooltip above spotlight
        // We need to know the tooltip height to position it correctly
        // Using estimated height of 280px for Project actions tooltip (based on content)
        const estimatedTooltipHeight = 280;

        // The Paper component has internal padding structure:
        // - Paper p: 3 (24px)
        // - Header has margin: -24px and padding: 32px
        // - Net effect: content is offset from Paper's left edge
        // Adjust to align the content with spotlight
        const contentOffsetAdjustment = 10;

        return {
          top: spotlightTop - 8 - estimatedTooltipHeight, // 8px gap above spotlight
          left: spotlightLeft - contentOffsetAdjustment, // Align content with spotlight left edge
        };
      }

      // Fallback if button not found
      return {
        top: rect.top - 13 - 8 - 280,
        left: rect.left - 13,
      };
    }

    if (isMyDesignsButton) {
      // My Designs button - position tooltip BELOW the button
      // The button spotlight has exact fit without adjustments
      const myDesignsBtn = document.getElementById('myDesignsButton');

      if (myDesignsBtn) {
        const btnRect = myDesignsBtn.getBoundingClientRect();

        // The Paper component has internal padding that offsets content
        // Adjust to align the content with button left edge
        const contentOffsetAdjustment = 0;

        return {
          top: btnRect.bottom + 8, // 8px gap below button
          left: btnRect.left - contentOffsetAdjustment, // Align content with button left edge
        };
      }

      // Fallback using rect (which might be from virtual anchor)
      return {
        top: rect.bottom + 8,
        left: rect.left,
      };
    }

    return null;
  }, [anchorEl, spotlightTarget]);

  
  // Sync Redux state on mount with current step and clean up sessionStorage
  useEffect(() => {
    dispatch(ReducersIndex.uiReducers.setTutorialStep(currentStep));
    
    // If we have a current step with a target, ensure spotlight is set on mount
    const step = steps[currentStep];
    if (step?.target) {
      dispatch(ReducersIndex.uiReducers.setSpotlightTarget(step.target));
    }
    
    // Clear sessionStorage after we've used it
    sessionStorage.removeItem('tutorialStep');
    sessionStorage.removeItem('continueGuide');
  }, []); // Empty deps - run only on mount, currentStep is from useState initial value

  // Callback when spotlight finishes rendering
  const handleSpotlightReady = useCallback(() => {
    if (pendingTooltipSetupRef.current) {
      pendingTooltipSetupRef.current();
      pendingTooltipSetupRef.current = null;
    }

    // For nav sections, projectName, actionButtons, projectActions, and myDesignsButton, mark tooltip as ready to show after spotlight is rendered
    const targets = Array.isArray(spotlightTarget) ? spotlightTarget : [spotlightTarget];
    const isNavTarget = targets.some(t =>
      ['floor_plan', 'ceiling', 'furnishing', 'acoustics', 'microphones', 'speakers', 'camera', 'equipment_list'].includes(t || '')
    );
    const isProjectNameTarget = targets.includes('projectName');
    const isActionButtonsTarget = targets.includes('actionButtons');
    const isProjectActionsTarget = targets.includes('saveButton') && targets.includes('exportButton') && targets.includes('designContainerQuoteButton');
    const isMyDesignsButtonTarget = targets.includes('myDesignsButton');

    if (isNavTarget || isProjectNameTarget || isActionButtonsTarget || isProjectActionsTarget || isMyDesignsButtonTarget) {
      // Small delay to ensure spotlight positioning is complete
      setTimeout(() => {
        setNavTooltipReady(true);
      }, 50);
    }
  }, [spotlightTarget]); // Add spotlightTarget as dependency

  // Helper function to handle nav section positioning
  const handleNavSectionPositioning = useCallback((targetElement: HTMLElement, step: any) => {
    // Don't start new positioning if we're already in transition
    if (isInTransition) {
      return;
    }

    setIsInTransition(true); // Mark that we're in transition

    // Clear any existing positioning timeouts
    if (positioningTimeoutRef.current) {
      clearTimeout(positioningTimeoutRef.current);
    }

    // Store the element ID for special positioning
    const elementId = targetElement.id;

      // Set the target view for nav sections
      const targetView = elementId.replace(/_/g, '');
      if (step?.menu && ['floor_plan', 'ceiling', 'furnishing', 'acoustics', 'microphones', 'speakers', 'camera', 'equipment_list'].includes(elementId)) {
        dispatch(ReducersIndex.uiReducers.setCurrentView(targetView));
      }

      // Close any open dropdowns that might interfere
      const event = new MouseEvent('click', { bubbles: true, cancelable: true });
      document.body.dispatchEvent(event);

      // Use the actual DOM element as anchor (no virtual anchor needed!)
      const currentElement = document.getElementById(elementId);
      if (!currentElement) {
        setIsPositioningNavSection(false);
        return;
      }

      // Simply set the real element as anchor - MUI Popper will handle positioning
      setAnchorEl(currentElement);
      setFadeGuide(true);

      setIsPositioningNavSection(false);
      setIsInTransition(false); // Mark transition as complete
  }, [dispatch, isInTransition]);

  // Handle step changes with memoization to prevent re-execution
  useEffect(() => {
    // Skip if we've already processed this step or if we're in transition
    if (lastProcessedStepRef.current === safeCurrentStep || isInTransition) {
      return;
    }

    // Mark this step as processed
    lastProcessedStepRef.current = safeCurrentStep;

    // Reset nav tooltip ready state when changing steps
    setNavTooltipReady(false);

    // Get the current guide step inside the effect to avoid dependency issues
    const step = steps[safeCurrentStep];

    // For actionButtons target, hide tooltip immediately to prevent flash
    const targets = Array.isArray(step?.target) ? step.target : [step?.target];
    if (targets.includes('actionButtons')) {
      // Use flushSync to force synchronous state update before continuing
      flushSync(() => {
        setHideGuideModal(true);
        setAnchorEl(null);
      });
    }

    // Activate the navigation section if menu is specified
    if (step?.menu) {
      // Convert menu name to view name (remove underscores)
      const viewName = step.menu.replace(/_/g, '');
      dispatch(ReducersIndex.uiReducers.setCurrentView(viewName));
      
      // Skip scrolling for nav sections as they handle it in handleNavSectionPositioning
      const isNavSectionMenu = ['floor_plan', 'ceiling', 'furnishing', 'acoustics', 'microphones', 'speakers', 'camera', 'equipment_list'].includes(step.menu);
      if (!isNavSectionMenu) {
        // Scroll to menu item after a small delay for non-nav sections
        setTimeout(() => {
          const targetElement = document.getElementById(step.menu);
          if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
    }
    
    // Always update spotlight target based on current step BEFORE executing action
    // This ensures spotlight is set for every step, including name design
    const targetToSet = step?.target || null;
    dispatch(ReducersIndex.uiReducers.setSpotlightTarget(targetToSet));

    // Execute action if defined - moved after spotlight is set to prevent flash
    // Actions may cause DOM mutations that trigger MutationObserver, so we want
    // the spotlight and tooltip to be positioned first
    if (step?.action && tutorialActions[step.action]) {
      tutorialActions[step.action](areaRef.current, dispatch);
    }

    // Find and highlight the target element if it exists
    if (step?.target) {

      // Handle multiple targets
      const targets = Array.isArray(step.target)
        ? step.target
        : [step.target];

      // Special handling for actionButtons - wait for element to exist
      if (targets.includes('actionButtons')) {
        // Set up pending callback that will be triggered by spotlight
        // (tooltip already hidden at line 710)
        pendingTooltipSetupRef.current = () => {
          const freshElement = document.getElementById('actionButtons');
          if (freshElement) {
            setAnchorEl(freshElement);
            setHideGuideModal(false);
          }
        };
        return; // Exit early, spotlight will trigger the callback
      }

      // Find the first available target immediately
      let targetElement: HTMLElement | null = null;
      for (const targetId of targets) {
        targetElement = document.getElementById(targetId) as HTMLElement;
        if (targetElement) {
          break;
        }
      }

      if (targetElement) {
        // Check if this is the Request Quote step - needs special positioning regardless of which button is the anchor
        const isRequestQuoteStep = step?.title === 'Project actions';

        // Check if this is a nav section - define it once at the top level
        const isNavSection = ['floor_plan', 'ceiling', 'furnishing', 'acoustics', 'microphones', 'speakers', 'camera', 'equipment_list'].includes(targetElement.id);

        // Special handling for Request Quote step
        if (isRequestQuoteStep && (targetElement.id === 'saveButton' || targetElement.id === 'exportButton' || targetElement.id === 'designContainerQuoteButton' || targetElement.id === 'duplicateButton' || targetElement.id === 'resetButton')) {
          // Find the Duplicate button (leftmost button in the row)
          const duplicateButton = document.getElementById('duplicateButton');
          const requestQuoteButton = document.getElementById('designContainerQuoteButton');

          if (duplicateButton && requestQuoteButton) {
            const duplicateRect = duplicateButton.getBoundingClientRect();
            const requestQuoteRect = requestQuoteButton.getBoundingClientRect();

            // Create anchor spanning the full button group, positioned 12px above them
            const anchorTop = duplicateRect.top - 12;

            const virtualAnchor = {
              getBoundingClientRect: () => ({
                top: anchorTop,
                left: duplicateRect.left,
                bottom: anchorTop + 1,
                right: requestQuoteRect.right,
                width: requestQuoteRect.right - duplicateRect.left,
                height: 1,
                x: duplicateRect.left,
                y: anchorTop,
                toJSON: () => ({})
              }),
              id: 'requestQuoteAnchor'
            };

            setAnchorEl(virtualAnchor as any);
            setFadeGuide(true);
          } else {
            // Fallback: use the target element itself
            setAnchorEl(targetElement);
            setFadeGuide(true);
          }
          return;
        }
        // Special handling for nav sections
        else if (isNavSection) {
          setIsPositioningNavSection(true);
          handleNavSectionPositioning(targetElement, step);
          return;
        }
        // Note: actionButtons is handled early at the top (line 750) because element may not exist yet
        // Special handling for Export button with smooth transition
        else if (targetElement.id === 'exportButton') {
          // Clear anchor first to prevent flashing
          setAnchorEl(null);
          setFadeGuide(false);

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
            const exportRect = targetElement.getBoundingClientRect();

            // Check if this is the Request Quote step by looking at the current tutorial step
            const currentTutorialStep = steps[safeCurrentStep];
            const isRequestQuoteStep = currentTutorialStep?.title === 'Project actions';

            let modalCenterX = exportRect.left + (exportRect.width / 2); // fallback to center over export
            let modalPositionY = exportRect.top - 10; // default position

            if (isRequestQuoteStep) {
              // Find the Duplicate button (leftmost) to align modal to left side of spotlight
              const duplicateButton = document.getElementById('duplicateButton');

              if (duplicateButton) {
                const duplicateRect = duplicateButton.getBoundingClientRect();
                const modalWidth = 420; // Modal width for Request quote step
                // Align modal left edge to spotlight left edge (duplicateButton.left - 10px padding)
                // Since Popper centers on modalCenterX, add half the modal width
                const newModalCenterX = (duplicateRect.left - 10) + (modalWidth / 2);
                modalCenterX = newModalCenterX;
                modalPositionY = exportRect.top - 280; // Position modal 280px above the buttons (moved up 10px)
              }
            }

            const virtualAnchor = {
              getBoundingClientRect: () => ({
                top: modalPositionY,
                left: modalCenterX,
                bottom: modalPositionY + 1,
                right: modalCenterX,
                width: 0,
                height: 1,
                x: modalCenterX,
                y: modalPositionY,
                toJSON: () => ({})
              }),
              id: 'exportButtonAnchor'
            };
              setAnchorEl(virtualAnchor as any);
              setFadeGuide(true);
            });
          });
        }
        // Special handling for Request Quote button with smooth transition
        else if (targetElement.id === 'designContainerQuoteButton') {
          // Find the Export button to use the leftmost button as anchor
          const exportButton = document.evaluate("//button[contains(text(), 'Export')]", document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue as Element;

          // Use Export button (leftmost) as the anchor since it's on the left side
          // The tooltip will appear below this anchor using placement='bottom-start'
          if (exportButton) {
            const virtualAnchor = {
              getBoundingClientRect: () => {
                const exportRect = exportButton.getBoundingClientRect();
                const requestQuoteRect = targetElement.getBoundingClientRect();

                // Create anchor just above the button group (where we want the tooltip bottom to be)
                const anchorTop = exportRect.top - 12; // 12px above the buttons

                return {
                  top: anchorTop,
                  left: exportRect.left,
                  bottom: anchorTop + 1,
                  right: requestQuoteRect.right,
                  width: requestQuoteRect.right - exportRect.left,
                  height: 1,
                  x: exportRect.left,
                  y: anchorTop,
                  toJSON: () => ({})
                };
              },
              id: 'requestQuoteAnchor'
            };
            setAnchorEl(virtualAnchor as any);
            setFadeGuide(true);
          } else {
            // Fallback: use the button itself
            setAnchorEl(targetElement);
            setFadeGuide(true);
          }
        }
        // Special handling for My Designs button with smooth transition
        else if (targetElement.id === 'myDesignsButton') {
          // Clear anchor first to prevent flashing
          setAnchorEl(null);
          setFadeGuide(false);

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
            const rect = targetElement.getBoundingClientRect();
            // Create a virtual anchor positioned at the bottom of the My Designs button
            const virtualAnchor = {
              getBoundingClientRect: () => ({
                top: rect.bottom,
                left: rect.left,
                bottom: rect.bottom + 1,
                right: rect.left + rect.width,
                width: rect.width,
                height: 1,
                x: rect.left,
                y: rect.bottom,
                toJSON: () => ({})
              }),
              id: 'myDesignsButtonBottom'
            };
              setAnchorEl(virtualAnchor as any);
              setFadeGuide(true);
            });
          });
        }
        // For name design screen, use the parent container for better alignment
        else if (step.isNameDesignScreen && targetElement.id === 'projectName') {
          // Clear anchor first to prevent flashing
          setAnchorEl(null);
          setFadeGuide(false);

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
            const parentSection = targetElement.parentElement as HTMLElement;
            if (parentSection) {
              setAnchorEl(parentSection);
            } else {
              setAnchorEl(targetElement);
            }
              setFadeGuide(true);
            });
          });
        } else if (!isNavSection && !isPositioningNavSection) {
          // For other elements, use anti-flashing technique too
          setAnchorEl(null);
          setFadeGuide(false);

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setAnchorEl(targetElement);
              setFadeGuide(true);
            });
          });
        }
        // Nav sections are handled by handleNavSectionPositioning, so skip them here
        
        // Don't add highlight class - no blue border needed
        
        // Scroll into view if needed (but not for actionButtons and nav sections)
        // Nav sections handle their own scrolling in handleNavSectionPositioning
        if (targetElement.id !== 'actionButtons' && !isNavSection) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          
          // Wait for scroll to complete before finalizing position
          setTimeout(() => {
            // Force a re-render to update the position after scroll
            setAnchorEl(prevAnchor => {
              // Return the same anchor to trigger re-evaluation
              return prevAnchor;
            });
          }, 500); // Wait for smooth scroll animation
        }
      } else {
        // If myDesignsButton wasn't found, try to find it by text content
        if (step.target === 'myDesignsButton') {
          const buttons = Array.from(document.querySelectorAll('button'));
          const myDesignsBtn = buttons.find(btn => btn.textContent?.includes('My designs') || btn.textContent?.includes('My Designs'));
          
          if (myDesignsBtn) {
            // Add an ID to it for future reference
            myDesignsBtn.id = 'myDesignsButton';
            setAnchorEl(myDesignsBtn);
          } else {
            setAnchorEl(null);
          }
        }
        // For welcome screen, center the modal
        // For name design screen, try to position near nav container
        else if (step.isWelcomeScreen) {
          setAnchorEl(null);
        } else if (step.isNameDesignScreen) {
          // Try to find the project name section or nav container as fallback
          const projectSection = document.querySelector('[id="projectName"]')?.parentElement as HTMLElement;
          if (projectSection) {
            setAnchorEl(projectSection);
          } else {
            const navContainer = document.querySelector('.nav-container') as HTMLElement;
            if (navContainer) {
              setAnchorEl(navContainer);
            } else {
              setAnchorEl(null);
            }
          }
        }
      }

      // No cleanup here - we handle it elsewhere
    } else {
      setAnchorEl(null);
    }

  }, [safeCurrentStep, dispatch]); // Remove area from dependencies to prevent re-runs when actions modify it
  
  // Window resize handler to force tooltip repositioning
  useEffect(() => {
    let resizeTimeout: NodeJS.Timeout | null = null;

    const handleResize = () => {
      // Debounce resize events to avoid excessive recalculations
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }

      resizeTimeout = setTimeout(() => {
        // Force tooltip to recalculate position by updating anchorEl
        setAnchorEl(prevAnchor => {
          if (!prevAnchor) return prevAnchor;

          // For real DOM elements, just return to trigger re-evaluation
          if (prevAnchor instanceof HTMLElement) {
            return prevAnchor;
          }

          // For virtual anchors, we need to recreate them with updated positions
          const step = steps[safeCurrentStep];
          if (!step?.target) return prevAnchor;

          // Type guard: check if prevAnchor has an id property (virtual anchor)
          const virtualAnchor = prevAnchor as any;
          if (!virtualAnchor.id) return prevAnchor;

          // Handle Request Quote step with multiple buttons
          if (virtualAnchor.id === 'requestQuoteAnchor') {
            const duplicateButton = document.getElementById('duplicateButton');
            const requestQuoteButton = document.getElementById('designContainerQuoteButton');

            if (duplicateButton && requestQuoteButton) {
              const duplicateRect = duplicateButton.getBoundingClientRect();
              const requestQuoteRect = requestQuoteButton.getBoundingClientRect();
              const anchorTop = duplicateRect.top - 12;

              return {
                getBoundingClientRect: () => ({
                  top: anchorTop,
                  left: duplicateRect.left,
                  bottom: anchorTop + 1,
                  right: requestQuoteRect.right,
                  width: requestQuoteRect.right - duplicateRect.left,
                  height: 1,
                  x: duplicateRect.left,
                  y: anchorTop,
                  toJSON: () => ({})
                }),
                id: 'requestQuoteAnchor'
              } as any;
            }
          }

          // Handle Export button anchor
          if (virtualAnchor.id === 'exportButtonAnchor') {
            const exportButton = document.getElementById('exportButton');
            if (exportButton) {
              const exportRect = exportButton.getBoundingClientRect();
              const currentTutorialStep = steps[safeCurrentStep];
              const isRequestQuoteStep = currentTutorialStep?.title === 'Project actions';

              let modalCenterX = exportRect.left + (exportRect.width / 2);
              let modalPositionY = exportRect.top - 10;

              if (isRequestQuoteStep) {
                const duplicateButton = document.getElementById('duplicateButton');
                if (duplicateButton) {
                  const duplicateRect = duplicateButton.getBoundingClientRect();
                  const modalWidth = 420;
                  const newModalCenterX = (duplicateRect.left - 10) + (modalWidth / 2);
                  modalCenterX = newModalCenterX;
                  modalPositionY = exportRect.top - 280;
                }
              }

              return {
                getBoundingClientRect: () => ({
                  top: modalPositionY,
                  left: modalCenterX,
                  bottom: modalPositionY + 1,
                  right: modalCenterX,
                  width: 0,
                  height: 1,
                  x: modalCenterX,
                  y: modalPositionY,
                  toJSON: () => ({})
                }),
                id: 'exportButtonAnchor'
              } as any;
            }
          }

          // Handle My Designs button anchor
          if (virtualAnchor.id === 'myDesignsButtonBottom') {
            const myDesignsButton = document.getElementById('myDesignsButton');
            if (myDesignsButton) {
              const rect = myDesignsButton.getBoundingClientRect();
              return {
                getBoundingClientRect: () => ({
                  top: rect.bottom,
                  left: rect.left,
                  bottom: rect.bottom + 1,
                  right: rect.left + rect.width,
                  width: rect.width,
                  height: 1,
                  x: rect.left,
                  y: rect.bottom,
                  toJSON: () => ({})
                }),
                id: 'myDesignsButtonBottom'
              } as any;
            }
          }

          // For all other cases, return the same anchor to trigger re-evaluation
          return prevAnchor;
        });
      }, 100); // 100ms debounce delay
    };

    // Add resize listener
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
    };
  }, [safeCurrentStep, steps]);

  // Separate cleanup effect that only runs on unmount
  useEffect(() => {
    return () => {
      // Clear all timeouts
      if (positioningTimeoutRef.current) {
        clearTimeout(positioningTimeoutRef.current);
      }
      // Clear spotlight and backdrop when component unmounts
      setShouldShowBackdrop(false);
      dispatch(ReducersIndex.uiReducers.setSpotlightTarget(null));
    };
  }, [dispatch]);

  const handleNext = () => {
    // Special handling for welcome screen - navigate to conference room designer
    if (currentGuideStep?.isWelcomeScreen) {
      // Store selected location if any
      if (selectedLocation) {
        sessionStorage.setItem('selectedLocationId', selectedLocation);
      } else {
        sessionStorage.removeItem('selectedLocationId');
      }

      // Save tutorial state to continue in conference room designer
      sessionStorage.setItem('tutorialStep', '1'); // Next step after welcome
      sessionStorage.setItem('continueGuide', 'true');

      // Clear all Redux state by reloading the page with a fresh conference room designer
      // This performs the same cleanup as "Create new design" in DesignsList
      window.location.href = '/conference_room_designer';
    } else if (currentGuideStep?.isNameDesignScreen) {
      // Then proceed to next step
      if (currentStep < steps.length - 1) {
        const nextStep = currentStep + 1;
        setCurrentStep(nextStep);
        dispatch(ReducersIndex.uiReducers.setTutorialStep(nextStep));
      } else {
        handleComplete();
      }
    } else if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      dispatch(ReducersIndex.uiReducers.setTutorialStep(nextStep));
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    // Prevent going back from Name design screen
    if (currentGuideStep?.isNameDesignScreen) {
      return;
    }

    if (currentStep > 0) {
      const previousStep = currentStep - 1;
      const previousGuideStep = steps[previousStep];

      // If going back to welcome screen, navigate to designs page
      if (previousGuideStep?.isWelcomeScreen) {
        sessionStorage.setItem('tutorialStep', previousStep.toString());
        sessionStorage.setItem('continueGuide', 'false');
        // Reset anchor element so it centers properly on designs page
        setAnchorEl(null);
        navigate('/designs');
      } else {
        setCurrentStep(previousStep);
        dispatch(ReducersIndex.uiReducers.setTutorialStep(previousStep));

        // When going backward, immediately show nav tooltip since spotlight is already positioned
        const targets = Array.isArray(previousGuideStep?.target) ? previousGuideStep.target : [previousGuideStep?.target];
        const isNavTarget = targets.some((t: string | null | undefined) =>
          ['floor_plan', 'ceiling', 'furnishing', 'acoustics', 'microphones', 'speakers', 'camera', 'equipment_list'].includes(t || '')
        );
        const isProjectNameTarget = targets.includes('projectName');
        const isActionButtonsTarget = targets.includes('actionButtons');
        const isProjectActionsTarget = targets.includes('saveButton') && targets.includes('exportButton') && targets.includes('designContainerQuoteButton');
        const isMyDesignsButtonTarget = targets.includes('myDesignsButton');
        if (isNavTarget || isProjectNameTarget || isActionButtonsTarget || isProjectActionsTarget || isMyDesignsButtonTarget) {
          setNavTooltipReady(true);
        }
      }
    }
  };

  const handleComplete = () => {
    setIsVisible(false);
    setShouldShowBackdrop(false); // Turn off backdrop visibility
    dispatch(ReducersIndex.uiReducers.setIsFirstTimeUser(false));
    dispatch(ReducersIndex.uiReducers.setSpotlightTarget(null));
    // Persist that the user has completed the tutorial
    localStorage.setItem('hasCompletedTutorial', 'true');
    if (onComplete) {
      onComplete();
    }
  };

  const handleSkip = () => {
    setIsVisible(false);
    setShouldShowBackdrop(false); // Turn off backdrop visibility
    dispatch(ReducersIndex.uiReducers.setSpotlightTarget(null));
    dispatch(ReducersIndex.uiReducers.setTutorialStep(0));
    dispatch(ReducersIndex.uiReducers.setIsFirstTimeUser(false));
    // Persist that the user has skipped/completed the tutorial
    localStorage.setItem('hasCompletedTutorial', 'true');
    if (onSkip) {
      onSkip();
    } else {
      handleComplete();
    }
  };


  // Only show if we have steps to show
  if (!steps || steps.length === 0) {
    return null;
  }

  // Don't show welcome screen on conference room designer route
  if (currentGuideStep?.isWelcomeScreen && location.pathname === '/conference_room_designer') {
    return null;
  }

  // Don't show name design screen on designs route
  if (currentGuideStep?.isNameDesignScreen && location.pathname === '/designs') {
    return null;
  }

  // Handle designs page rendering specifically
  if (showOnDesignsPage) {
    // Only show on designs page for first-time users
    const hasCompletedTutorial = localStorage.getItem('hasCompletedTutorial') === 'true';
    if (hasCompletedTutorial || !isFirstTimeUser) {
      return null;
    }
  } else {
    // MODIFIED: For other routes (conference room designer), show if explicitly triggered via spotlight
    // AND hasCompletedTutorial is false (allows triggering from info icon)
    // REVERT: Remove hasCompletedTutorial check to restore original behavior
    const hasCompletedTutorial = localStorage.getItem('hasCompletedTutorial') === 'true';
    if (!spotlightTarget || hasCompletedTutorial) {
      return null;
    }
  }

  if (!isVisible) {
    return null;
  }

  // Ensure currentGuideStep exists
  if (!currentGuideStep) {
    return null;
  }

  // Determine positioning and styling based on screen type
  const isWelcomeScreen = currentGuideStep?.isWelcomeScreen;
  const isNameDesignScreen = currentGuideStep?.isNameDesignScreen;
  const isActionButtonsStep = currentGuideStep?.target === 'actionButtons';
  // Name design screen should be positioned relative to projectName element, not centered
  const isCenteredModal = isWelcomeScreen;

  // Check if this is a nav section, projectName, actionButtons, or projectActions that should use fixed positioning
  const isNavSection = anchorEl instanceof HTMLElement &&
    ['floor_plan', 'ceiling', 'furnishing', 'acoustics', 'microphones', 'speakers', 'camera', 'equipment_list'].includes(anchorEl.id);

  const isProjectNameSection = anchorEl instanceof HTMLElement &&
    (anchorEl.id === 'projectName' || anchorEl.querySelector('#projectName'));

  const isActionButtonsSection = anchorEl instanceof HTMLElement && anchorEl.id === 'actionButtons';

  // Check if this is Project actions (multiple buttons)
  // anchorEl will have id 'requestQuoteAnchor' for this step
  const isProjectActionsSection = (anchorEl as any)?.id === 'requestQuoteAnchor';

  // Check if this is My Designs button
  const isMyDesignsButtonSection = (anchorEl as any)?.id === 'myDesignsButtonBottom' ||
    (anchorEl instanceof HTMLElement && anchorEl.id === 'myDesignsButton');

  // Use fixed positioning for nav sections, projectName, actionButtons, projectActions, and myDesignsButton
  const useFixedPosition = isNavSection || isProjectNameSection || isActionButtonsSection || isProjectActionsSection || isMyDesignsButtonSection;

  // Calculate fixed position for nav sections, projectName, actionButtons, projectActions, and myDesignsButton
  const fixedPosition = calculateTooltipPosition();

  // Get the anchor element for Tooltip
  const tooltipAnchor = isCenteredModal ? null : isActionButtonsStep ? null : isNameDesignScreen ?
    document.getElementById('projectName') : anchorEl;

  return (
    <>
      {/* Guide tooltip/modal - Use fixed positioning for nav sections, projectName, actionButtons, projectActions, and myDesignsButton */}
      {useFixedPosition && fixedPosition && !hideGuideModal && navTooltipReady ? (
        // Fixed position Paper - no Tooltip wrapper needed
        <Paper
          elevation={0}
          sx={{
            position: 'fixed',
            top: `${fixedPosition.top}px`,
            left: `${fixedPosition.left}px`,
            p: 3,
            width: isProjectActionsSection ? 460 : (isNameDesignScreen ? 480 : 360),
            maxWidth: '90vw',
            borderRadius: '12px',
            backgroundColor: theme => theme.palette.background.paper,
            border: theme => `2px solid ${theme.palette.divider}`,
            boxShadow: theme => theme.palette.mode === 'dark'
              ? '0 8px 24px rgba(0,0,0,0.5)'
              : '0 8px 24px rgba(0,0,0,0.15)',
            zIndex: 1300,
            overflow: 'hidden',
          }}
        >
            {/* Header with title and skip guide */}
            <Box
              sx={{
                backgroundColor: theme => theme.palette.background.paper,
                color: theme => theme.palette.text.primary,
                padding: '24px 32px 16px',
                borderBottom: 'none',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                margin: '-24px -24px 0 -24px',
              }}>
              <WalkthroughTitle>
                {currentGuideStep.title || 'Tutorial'}
              </WalkthroughTitle>
              <SkipGuide onClick={handleSkip} />
            </Box>

            {/* Content */}
            <Box
              sx={{
                backgroundColor: theme => theme.palette.background.paper,
                padding: '0 32px',
                minHeight: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                margin: '0 -24px',
              }}
            >
              {isNameDesignScreen ? (
                <>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '14px',
                      lineHeight: '1.5',
                      color: theme => theme.palette.text.primary,
                      fontFamily: '"Open Sans"',
                      mb: 3
                    }}
                  >
                    {currentGuideStep.content}
                  </Typography>
                  <Box sx={{ height: '15px' }} />
                  {/* Design name input */}
                  <Box sx={{ mb: 2 }}>
                    <InputLabel optional>
                      Design name
                    </InputLabel>
                    <Input
                      fullWidth
                      placeholder="Enter design name"
                      value={designName}
                      onChange={(e) => {
                        const newName = e.target.value;
                        setDesignName(newName);
                        // Update the project name in Redux
                        dispatch(ReducersIndex.container.setProject({ name: newName }));
                      }}
                    />
                  </Box>
                  <Box sx={{ height: '10px' }} />
                  {/* Add Workplace location (collapsible) */}
                  <Box sx={{marginBottom: '20px'}}>
                    <Box
                      onClick={() => setLocationExpanded(!locationExpanded)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        mb: locationExpanded ? 2 : 0,
                        '&:hover': {
                          opacity: 0.8,
                        },
                      }}
                    >
                      <ChevronRightIcon sx={{
                        fontSize: 20,
                        padding: 0,
                        marginLeft: "-5px",
                        transform: locationExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                      }} />
                      <InputLabel
                        optional
                        sx={{
                          marginBottom: 0,
                          marginTop: '6px',
                          cursor: 'pointer',
                        }}
                      >
                        Add Workplace location
                      </InputLabel>
                    </Box>

                    {/* Expandable location section */}
                    {locationExpanded && (
                      <Box sx={{ mt: 2 }}>
                        <InputLabel>
                          Location
                        </InputLabel>
                        {/* LocationDropdown component */}
                        <LocationDropdown
                          organizationId={sessionStorage.getItem('selectedOrgId') || ''}
                          value={selectedLocation}
                          onChange={setSelectedLocation}
                          placeholder="No location selected (optional)"
                        />
                      </Box>
                    )}
                  </Box>
                </>
              ) : (
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '14px',
                    lineHeight: '150%',
                    fontWeight: 400,
                    letterSpacing: '-0.28px',
                    color: theme => theme.palette.text.primary,
                    fontFamily: '"Open Sans"',
                    mb: 3,
                    '& strong': {
                      fontWeight: 600,
                    }
                  }}
                  dangerouslySetInnerHTML={{ __html: currentGuideStep.content }}
                />
              )}
            </Box>
            <Box sx={{ height: '20px' }} />
            {/* Navigation buttons */}
            <Box
              sx={{
                backgroundColor: theme => theme.palette.background.paper,
                padding: '0 32px',
                minHeight: 'auto',
                display: 'flex', gap: '10px', width: '100%', justifyContent: 'space-between',
                margin: '0 -24px',
              }}>
              <Box
                component="button"
                onClick={handlePrevious}
                disabled={isNameDesignScreen ? safeCurrentStep === 1 : safeCurrentStep === 0}
                sx={{
                        backgroundColor: theme => theme.palette.mode === 'light' ? '#ffffff' : theme.palette.background.paper,
                        color: theme => (isNameDesignScreen || safeCurrentStep === 0) ? theme.palette.text.disabled : theme.palette.text.primary,
                        border: theme => `1px solid ${theme.palette.divider}`,
                        borderRadius: '6px',
                        height: '44px',
                        fontSize: '12px',
                        fontFamily: '"Open Sans"',
                        fontWeight: 600,
                        cursor: (isNameDesignScreen || safeCurrentStep === 0) ? 'not-allowed' : 'pointer',
                        opacity: (isNameDesignScreen || safeCurrentStep === 0) ? 0.5 : 1,
                        minWidth: '80px',
                        pointerEvents: (isNameDesignScreen || safeCurrentStep === 0) ? 'none' : 'auto',
                        '&:hover': {
                          backgroundColor: theme => (isNameDesignScreen || safeCurrentStep === 0) ? (theme.palette.mode === 'light' ? '#ffffff' : theme.palette.background.paper) : theme.palette.action.hover,
                          borderColor: theme => theme.palette.divider
                        }
                }}
              >
                Back
              </Box>
              <Box
                component="button"
                onClick={
                  safeCurrentStep === steps.length - 1
                    ? handleSkip
                    : handleNext
                }
                sx={{
                  backgroundColor: theme => theme.palette.primary.main,
                  color: theme => theme.palette.primary.contrastText,
                  border: 'none',
                  borderRadius: '6px',
                  height: '44px',
                  fontSize: '12px',
                  fontFamily: '"Open Sans"',
                  fontWeight: 600,
                  cursor: 'pointer',
                  flex: 1,
                  '&:hover': {
                    backgroundColor: theme => theme.palette.primary.dark
                  }
                }}
              >
                {safeCurrentStep === steps.length - 1 ? 'Finish guide' : (isNameDesignScreen ? (currentGuideStep?.buttonText || 'Next') : 'Next')}
              </Box>
            </Box>
            <Box sx={{height: '20px'}} />
              {/* Progress dots */}
              <Box
                sx={{
                  backgroundColor: theme => theme.palette.background.paper,
                  padding: '0 32px',
                  minHeight: 'auto',
                  display: 'flex', gap: 1.5, width: '100%',
                  marginBottom: "20px",
                  margin: '0 -24px 20px -24px',
                }}
              >
                <Box sx={{ display: 'flex', gap: "3px", justifyContent: 'space-between', width: '100%', mt: 2 }}>
                  {Array.from({ length: steps.length || 0 }, (_, i) => (
                    <Box
                      key={i}
                      sx={{
                        flex: i === safeCurrentStep ? 3 : 1,
                        height: 4,
                        borderRadius: 2,
                        backgroundColor: theme => i === safeCurrentStep ? theme.palette.primary.main :
                                        i < safeCurrentStep ? theme.palette.primary.light : theme.palette.action.disabled,
                        transition: 'all 0.3s ease'
                      }}
                    />
                  ))}
                </Box>
              </Box>
          </Paper>
      ) : (
        // Tooltip for all other cases
        <Tooltip
          key={`guide-step-${safeCurrentStep}`}
          open={!hideGuideModal}
          arrow={!isWelcomeScreen}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          placement={
            isNameDesignScreen ? 'right-start' :
            anchorEl?.id === 'actionButtons' ? 'right-start' :
            anchorEl?.id === 'requestQuoteAnchor' ? 'bottom-start' :
            anchorEl?.id === 'exportButtonAnchor' ? 'bottom' :
            anchorEl?.id === 'myDesignsButtonBottom' ? 'bottom-start' :
            (anchorEl?.id === 'designContainerQuoteButton' || anchorEl?.id === 'exportButton') ? 'bottom-start' :
            anchorEl && !isCenteredModal ? 'right-start' :
            'bottom'
          }
        slotProps={{
          popper: {
            anchorEl: anchorEl,
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: isNameDesignScreen ? [0, 12] :
                          anchorEl?.id === 'actionButtons' ? [0, 12] :
                          anchorEl?.id === 'requestQuoteAnchor' ? [0, 0] :
                          anchorEl?.id === 'exportButtonAnchor' ? [10, 8] :
                          anchorEl?.id === 'myDesignsButtonBottom' ? [10, 8] :
                          (anchorEl?.id === 'designContainerQuoteButton' || anchorEl?.id === 'exportButton') ? [10, 8] :
                          [0, 12],
                },
              },
              {
                name: 'preventOverflow',
                enabled: anchorEl?.id !== 'actionButtons',
                options: {
                  boundary: 'viewport',
                  padding: 8,
                },
              },
            ],
            sx: isCenteredModal ? {
              position: 'fixed !important',
              top: '50% !important',
              left: '50% !important',
              transform: 'translate(-50%, -50%) !important',
              zIndex: 1400
            } : {
              zIndex: (isCenteredModal || isNameDesignScreen) ? 1400 : 1300
            }
          },
          tooltip: {
            sx: {
              backgroundColor: theme => theme.palette.background.paper,
              maxWidth: 'none',
              padding: 0,
              margin: 0,
              border: theme => `2px solid ${theme.palette.divider}`,
              borderRadius: '12px',
              boxShadow: theme => theme.palette.mode === 'dark'
                ? '0 8px 24px rgba(0,0,0,0.5)'
                : '0 8px 24px rgba(0,0,0,0.15)',
            }
          },
          arrow: {
            sx: {
              fontSize: '1.5rem', // Makes arrow larger
              color: theme => theme.palette.background.paper,
              '&::before': {
                border: theme => `2px solid ${theme.palette.divider}`,
              }
            }
          }
        }}
        title={
        <Fade in={true} timeout={300}>
          <Paper
            elevation={0}
            sx={{
              p: (isCenteredModal || isNameDesignScreen) ? 0 : 3,
              width: isWelcomeScreen ? 490 : isNameDesignScreen ? 480 : (steps[safeCurrentStep]?.title === 'Project actions' ? 460 : 360),
              maxWidth: '90vw',
              borderRadius: '12px', // Consistent rounded corners for all guide modals
              position: 'relative',
              backgroundColor: 'transparent', // Border/shadow handled by Tooltip wrapper
              overflow: 'hidden', // Ensure corners are properly clipped
              '& > *:first-of-type': {
                borderRadius: '12px 12px 0 0', // Ensure first child respects border radius
              },
              '& > *:last-child': {
                borderRadius: '0 0 12px 12px', // Ensure last child respects border radius
              }
            }}
          >
            {/* Special layout for welcome/name screens */}
            {(isCenteredModal || isNameDesignScreen) ? (
              <>
                {/* Header */}
                <Box
                  sx={{
                    backgroundColor: theme => theme.palette.background.paper,
                    color: theme => theme.palette.text.primary,
                    padding: isWelcomeScreen ? '32px 32px 24px' : '24px 32px 16px',
                    borderBottom: 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start'
                  }}
                >
                  <WalkthroughTitle>
                    {currentGuideStep.title}
                  </WalkthroughTitle>
                  <SkipGuide onClick={handleSkip} />
                </Box>

                {/* Content */}
                <Box
                  sx={{
                    backgroundColor: theme => theme.palette.background.paper,
                    padding: '0 32px',
                    minHeight: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  {isWelcomeScreen ? (
                    <>
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: '14px',
                          lineHeight: '150%',
                          fontWeight: 400,
                          letterSpacing: '-0.28px',
                          color: theme => theme.palette.text.primary,
                          fontFamily: '"Open Sans"',
                          mb: 3,
                        }}
                      >
                        {currentGuideStep.content}
                      </Typography>
                      
                      {currentGuideStep.subtitle && (
                        <Typography
                          variant="h6"
                          sx={{
                            marginTop: '20px',
                            fontSize: '14px',
                            fontWeight: 600,
                            lineHeight: '150%',
                            letterSpacing: '-0.28px',
                            color: theme => theme.palette.text.primary,
                            fontFamily: '"Open Sans"',
                            mb: 2
                          }}
                        >
                          {currentGuideStep.subtitle}
                        </Typography>
                      )}
                    </>
                  ) : null}
                </Box>

                {/* Footer */}
                <Box
                  sx={{
                    backgroundColor: theme => theme.palette.background.paper,
                    borderTop: 'none',
                    padding: isWelcomeScreen ? '24px 32px 32px' : '0 32px 40px',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 0
                  }}
                >
                  {isWelcomeScreen ? (
                    <>
                      {/* Button row */}
                      <Box sx={{ display: 'flex', gap: '10px', width: '100%', justifyContent: 'space-between'}}>
                        <Box
                          component="button"
                          onClick={handlePrevious}
                          disabled={safeCurrentStep === 0}
                          sx={{
                            backgroundColor: theme => theme.palette.mode === 'light' ? '#ffffff' : theme.palette.background.paper,
                            color: theme => safeCurrentStep === 0 ? theme.palette.text.disabled : theme.palette.text.primary,
                            border: theme => `1px solid ${theme.palette.divider}`,
                            borderRadius: '6px',
                            height: '44px',
                            fontSize: '12px',
                            fontFamily: '"Open Sans"',
                            fontWeight: 600,
                            cursor: safeCurrentStep === 0 ? 'not-allowed' : 'pointer',
                            minWidth: '120px',
                            '&:hover': {
                              backgroundColor: theme => safeCurrentStep === 0 ? (theme.palette.mode === 'light' ? '#ffffff' : theme.palette.background.paper) : theme.palette.action.hover,
                              borderColor: theme => theme.palette.divider
                            }
                          }}
                        >
                          Back
                        </Box>
                        <Box
                          component="button"
                          onClick={handleNext}
                          sx={{
                            backgroundColor: theme => theme.palette.primary.main,
                            color: theme => theme.palette.primary.contrastText,
                            border: 'none',
                            borderRadius: '6px',
                            height: '44px',
                            fontSize: '12px',
                            fontFamily: '"Open Sans"',
                            fontWeight: 600,
                            cursor: 'pointer',
                            flex: 1,
                            '&:hover': {
                              backgroundColor: theme => theme.palette.primary.dark
                            }
                          }}
                        >
                          {currentGuideStep?.buttonText || 'Create design'}
                        </Box>
                      </Box>
                      
                      {/* Progress dots */}
                      <Box sx={{ margin: '25px 0 0 0', display: 'flex', gap: "3px", justifyContent: 'space-between', width: '100%', mt: 2 }}>
                        {Array.from({ length: steps.length || 14 }, (_, i) => (
                          <Box
                            key={i}
                            sx={{
                              flex: i === safeCurrentStep ? 3 : 1,
                              height: 6,
                              borderRadius: 3,
                              mt: 5,
                              backgroundColor: theme => i === safeCurrentStep ? theme.palette.primary.main : 
                                              i < safeCurrentStep ? theme.palette.primary.light : theme.palette.action.disabled,
                              transition: 'all 0.3s ease'
                            }}
                          />
                        ))}
                      </Box>
                    </>
                  ) : null}
                </Box>
              </>
            ) : (
              // Standard guide tooltip layout for non-centered screens
              <>
                {/* Header with title and skip guide */}
                <Box                   
                  sx={{
                    backgroundColor: theme => theme.palette.background.paper,
                    color: theme => theme.palette.text.primary,
                    padding: isWelcomeScreen ? '32px 32px 24px' : '24px 32px 16px',
                    borderBottom: 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start'
                  }}>
                  <WalkthroughTitle>
                    {currentGuideStep.title || 'Tutorial'}
                  </WalkthroughTitle>
                  <SkipGuide onClick={handleSkip} />
                </Box>

                {/* Content */}
                <Box
                  sx={{
                    backgroundColor: theme => theme.palette.background.paper,
                    padding: '0 32px',
                    minHeight: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: '14px',
                          lineHeight: '150%',
                          fontWeight: 400,
                          letterSpacing: '-0.28px',
                          color: theme => theme.palette.text.primary,
                          fontFamily: '"Open Sans"',
                          mb: 3,
                          '& strong': {
                            fontWeight: 600,
                          }
                        }}
                        dangerouslySetInnerHTML={{ __html: currentGuideStep.content }}
                      />
                </Box>
                <Box sx={{ height: '20px' }} />
                {/* Navigation buttons - Regular tutorial steps */}
                <Box 
                  sx={{ 
                    backgroundColor: theme => theme.palette.background.paper,
                    padding: '0 32px',
                    minHeight: 'auto',
                    display: 'flex', gap: '10px', width: '100%', justifyContent: 'space-between' }}>
                  <Box
                    component="button"
                    onClick={handlePrevious}
                    disabled={safeCurrentStep === 0}
                    sx={{
                            backgroundColor: theme => theme.palette.mode === 'light' ? '#ffffff' : theme.palette.background.paper,
                            color: theme => safeCurrentStep === 0 ? theme.palette.text.disabled : theme.palette.text.primary,
                            border: theme => `1px solid ${theme.palette.divider}`,
                            borderRadius: '6px',
                            height: '44px',
                            fontSize: '12px',
                            fontFamily: '"Open Sans"',
                            fontWeight: 600,
                            cursor: safeCurrentStep === 0 ? 'not-allowed' : 'pointer',
                            opacity: safeCurrentStep === 0 ? 0.5 : 1,
                            minWidth: '80px',
                            pointerEvents: safeCurrentStep === 0 ? 'none' : 'auto',
                            '&:hover': {
                              backgroundColor: theme => safeCurrentStep === 0 ? (theme.palette.mode === 'light' ? '#ffffff' : theme.palette.background.paper) : theme.palette.action.hover,
                              borderColor: theme => theme.palette.divider
                            }
                    }}
                  >
                    Back
                  </Box>
                  <Box
                    component="button"
                    onClick={
                      safeCurrentStep === steps.length - 1
                        ? handleSkip
                        : handleNext
                    }
                    sx={{
                      backgroundColor: theme => theme.palette.primary.main,
                      color: theme => theme.palette.primary.contrastText,
                      border: 'none',
                      borderRadius: '6px',
                      height: '44px',
                      fontSize: '12px',
                      fontFamily: '"Open Sans"',
                      fontWeight: 600,
                      cursor: 'pointer',
                      flex: 1,
                      '&:hover': {
                        backgroundColor: theme => theme.palette.primary.dark
                      }
                    }}
                  >
                    {safeCurrentStep === steps.length - 1 ? 'Finish guide' : 'Next'}
                  </Box>
                </Box>
                <Box sx={{height: '20px'}} />
                  {/* Progress dots for regular tutorial steps */}
                  <Box
                    sx={{ 
                      backgroundColor: theme => theme.palette.background.paper,
                      padding: '0 32px',
                      minHeight: 'auto',
                      display: 'flex', gap: 1.5, width: '100%',
                      marginBottom: "20px"
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: "3px", justifyContent: 'space-between', width: '100%', mt: 2 }}>
                      {Array.from({ length: steps.length || 0 }, (_, i) => (
                        <Box
                          key={i}
                          sx={{
                            flex: i === safeCurrentStep ? 3 : 1,
                            height: 4,
                            borderRadius: 2,
                            backgroundColor: theme => i === safeCurrentStep ? theme.palette.primary.main : 
                                            i < safeCurrentStep ? theme.palette.primary.light : theme.palette.action.disabled,
                            transition: 'all 0.3s ease'
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
              </>
            )}
          </Paper>
        </Fade>
        }
      >
        <Box sx={{ display: 'none' }} />
        </Tooltip>
      )}

      {/* Backdrop for welcome screen */}
      {isWelcomeScreen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: theme => theme.palette.mode === 'dark' 
              ? 'rgba(0, 0, 0, 0.7)' 
              : 'rgba(0, 0, 0, 0.5)',
            zIndex: 1399,
          }}
          onClick={handleSkip}
        />
      )}

      {/* Precise backdrop - covers everything except the active nav section */}
      {!isWelcomeScreen && shouldShowBackdrop && (
        <PreciseBackdrop
          spotlightTarget={spotlightTarget || ''}
          onSpotlightReady={handleSpotlightReady}
        />
      )}

    </>
  );
};

export default WalkthroughGuide;